import React, {useEffect, useRef, useState} from 'react'
import {
  AU_FIXED_LINE_REGEX,
  formatDate,
  formatDayMonth,
  ScaffoldSubmission,
  ScaffoldSubmissionAgent,
  isSharedApplication,
} from '../renterHomeUtils'
import {getPlaceDetails, getStreetViewImageUrl} from '../../../api'
import ApplicationContactModal, {ApplicationContact, ContactAction} from './ApplicationContactModal'

type ApplicationsTabProps = {
  sessionId: string
  submissions: ScaffoldSubmission[]
  onSubmit: () => void
  onRestoreAccess: (submissionId: string) => void
  onRevoke: (submissionId: string) => void
  revokingSubmissionId?: string | null
  restoringSubmissionId?: string | null
  applicationType?: string
}

const ApplicationsTab: React.FC<ApplicationsTabProps> = ({
  sessionId,
  submissions,
  onSubmit,
  onRevoke,
  onRestoreAccess,
  revokingSubmissionId,
  restoringSubmissionId,
  applicationType
}) => {
  const [openMenuSubmissionId, setOpenMenuSubmissionId] = useState<string | null>(null)
  const hasPendingSubmissionAction = Boolean(revokingSubmissionId || restoringSubmissionId)

  return (
    <section className='renter-home-section'>
      <div className='renter-section-kicker'>
        {isSharedApplication(applicationType) ? 'SUBMISSIONS' : 'YOUR SUBMISSIONS'}
      </div>

      {submissions.length === 0 ? (
        <SubmissionEmptyState onSubmit={onSubmit} />
      ) : (
        <div className='renter-submission-list'>
          {submissions.map((submission) => (
            <SubmissionCard
              key={submission.id}
              sessionId={sessionId}
              submission={submission}
              onRevokeAccess={onRevoke}
              onRestoreAccess={onRestoreAccess}
              isRevoking={revokingSubmissionId === submission.id}
              isRestoring={restoringSubmissionId === submission.id}
              hasPendingAction={hasPendingSubmissionAction}
              isMenuOpen={openMenuSubmissionId === submission.id}
              onMenuToggle={() =>
                setOpenMenuSubmissionId((currentId) =>
                  currentId === submission.id ? null : submission.id
                )
              }
              onMenuClose={() => setOpenMenuSubmissionId(null)}
              applicationType={applicationType}
            />
          ))}
        </div>
      )}
    </section>
  )
}

type SubmissionEmptyStateProps = {
  onSubmit: () => void
}

const SubmissionEmptyState: React.FC<SubmissionEmptyStateProps> = ({ onSubmit }) => (
  <div className='renter-submissions-empty'>
    <div className='renter-empty-icon' aria-hidden='true'>
      <i className='bi bi-house-door'></i>
    </div>
    <h3>No submissions yet</h3>
    <p>Your Doora Card is ready. Submit it to a property to start your rental journey.</p>
    <button type='button' className='renter-gradient-btn' onClick={onSubmit}>
          <i className='bi bi-plus-lg' aria-hidden='true'></i>
          Submit to your first property
    </button>
  </div>
)

const toContactAgent = (agent: ScaffoldSubmissionAgent): ApplicationContact => ({
  name: agent.name?.trim() || 'Agent',
  phone: agent.phone?.trim() || undefined,
  email: agent.email?.trim() || undefined,
})

const getSubmissionContacts = (submission: ScaffoldSubmission): ApplicationContact[] => {
  const contacts = (submission.agents ?? [])
    .map(toContactAgent)
    .filter((agent) => agent.name || agent.phone || agent.email)

  if (contacts.length > 0) {
    return contacts
  }

  return [
    {
      name: submission.agentName?.trim() || 'Agent',
      phone: submission.agentPhone?.trim() || undefined,
      email: submission.agentEmail?.trim() || undefined,
    },
  ]
}

const getContactCountLabel = (count: number) => (count > 1 ? ` (${count})` : '')

const getAgentSummary = (contacts: ApplicationContact[]) => {
  if (contacts.length === 0) {
    return 'Agent'
  }

  if (contacts.length === 1) {
    return contacts[0].name
  }

  return `${contacts[0].name} + ${contacts.length - 1} ${
    contacts.length === 2 ? 'agent' : 'agents'
  }`
}

const getMissingEmailAgentCount = (
  submission: ScaffoldSubmission,
  contacts: ApplicationContact[]
) => {
  if (submission.agents && submission.agents.length > 0) {
    return submission.agents.filter((agent) => !agent.email?.trim()).length
  }

  return contacts.filter((agent) => !agent.email?.trim()).length
}

const getNoEmailMessage = (
  submission: ScaffoldSubmission,
  contacts: ApplicationContact[],
  isLandlineFlow: boolean
) => {
  const hasMultipleAgentsWithoutEmail = getMissingEmailAgentCount(submission, contacts) > 1

  if (isLandlineFlow) {
    return hasMultipleAgentsWithoutEmail
      ? 'Admin to input agent emails'
      : 'Admin to input Agent email'
  }

  return hasMultipleAgentsWithoutEmail
    ? "Agents haven't shared their emails yet"
    : "Agent hasn't shared their email yet"
}

type SubmissionCardProps = {
  sessionId: string
  submission: ScaffoldSubmission
  onRevokeAccess: (submissionId: string) => void
  onRestoreAccess: (submissionId: string) => void
  isRevoking: boolean
  isRestoring: boolean
  hasPendingAction: boolean
  isMenuOpen: boolean
  onMenuToggle: () => void
  onMenuClose: () => void
  applicationType?: string
}

const SubmissionCard: React.FC<SubmissionCardProps> = ({
  sessionId,
  submission,
  onRevokeAccess,
  onRestoreAccess,
  isRevoking,
  isRestoring,
  hasPendingAction,
  isMenuOpen,
  onMenuToggle,
  onMenuClose,
  applicationType
}) => {
  const [streetViewSrc, setStreetViewSrc] = useState<string | null>(null)
  const [contactAction, setContactAction] = useState<ContactAction | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const isViewed = submission.status === 'viewed'
  const isAwaitingEmail = submission.status === 'awaiting_email'
  const isNotSent = submission.status === 'not_sent'
  const isRevoked = submission.status === 'revoked'
  const isExpired = submission.status === 'expired'
  const canRevoke = !isRevoked && !isExpired && !hasPendingAction
  const hasExpiredAccessWindow = isExpired || submission.expiresInDays <= 0
  const canRestore = isRevoked && !hasExpiredAccessWindow && !hasPendingAction
  const contacts = getSubmissionContacts(submission)
  const callContacts = contacts.filter((agent) => agent.phone)
  const emailContacts = contacts.filter((agent) => agent.email)
  const isLandlineFlow = callContacts.some((agent) => AU_FIXED_LINE_REGEX.test(agent.phone ?? ''))
  const agentSummary = getAgentSummary(contacts)
  const hasMultipleAgents = contacts.length > 1
  const submittedByName = submission.submittedByName || submission.applicationFullName

  useEffect(() => {
    if (!isMenuOpen) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        onMenuClose()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [isMenuOpen, onMenuClose])

  useEffect(() => {
    if (!sessionId || !submission.placeId) {
      setStreetViewSrc(null)
      return
    }

    let cancelled = false

    const loadStreetView = async () => {
      try {
        const details = await getPlaceDetails(sessionId, submission.placeId!)
        if (cancelled) return
        if (details.latitude && details.longitude) {
          const blobUrl = await getStreetViewImageUrl(
            sessionId,
            details.latitude,
            details.longitude
          )
          if (!cancelled) {
            setStreetViewSrc(blobUrl)
          }
        }
      } catch {
        if (!cancelled) {
          setStreetViewSrc(null)
        }
      }
    }

    loadStreetView()

    return () => {
      cancelled = true
    }
  }, [sessionId, submission.placeId])

  return (
    <>
      <article className={`renter-submission-card ${isRevoked ? 'disabled' : ''}`}>
        <div className='inner'>
          <div className='photo'>
            {streetViewSrc ? (
              <img
                src={streetViewSrc}
                alt={submission.address}
                className='renter-property-streetview'
              />
            ) : (
              <svg
                width='40'
                height='40'
                viewBox='0 0 20 20'
                fill='none'
                stroke='rgba(11,15,26,0.15)'
                strokeWidth='1.4'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M3 10l7-6 7 6v7a1 1 0 01-1 1h-4v-5H8v5H4a1 1 0 01-1-1v-7z'></path>
              </svg>
            )}
            {submission.expiresInDays > 0 && (
              <div className={`expiresIn ${submission.expiresInDays <= 7 ? 'urgent' : ''}`}>
                <svg
                  width='11'
                  height='11'
                  viewBox='0 0 20 20'
                  fill='none'
                  stroke='#fff'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <circle cx='10' cy='10' r='7'></circle>
                  <path d='M10 6v4l2.5 2'></path>
                </svg>
                Expires in {submission.expiresInDays} days
              </div>
            )}
          </div>
          <div className='body'>
            <div className='address-section'>
              <div className='wrap'>
                <div className='title'>{submission.address}</div>
                {isSharedApplication(applicationType) && submittedByName ? (
                  <div className='submitted'>Submitted by {submittedByName}</div>
                ) : null}
                {isSharedApplication(applicationType) && (submission.includedMemberCount ?? 0) > 1 ? (
                  <div className='submitted'>
                    Includes {submission.includedMemberCount} household members
                  </div>
                ) : null}
                <div className='submitted'>Submitted {formatDate(submission.submittedAt)}</div>
              </div>
              <div className='menu' ref={menuRef}>
                {!isRevoked && (
                  <button
                    type='button'
                    aria-label='Submission actions'
                    aria-expanded={isMenuOpen}
                    onClick={onMenuToggle}
                  >
                    <svg
                      width='18'
                      height='18'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                      stroke='currentColor'
                      strokeWidth='0'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <circle cx='5' cy='10' r='1.5'></circle>
                      <circle cx='10' cy='10' r='1.5'></circle>
                      <circle cx='15' cy='10' r='1.5'></circle>
                    </svg>
                  </button>
                )}
                {isMenuOpen && (
                  <div className='dropdown'>
                    <button
                      className='revoke'
                      type='button'
                      role='menuitem'
                      disabled={!canRevoke}
                      onClick={() => {
                        onMenuClose()
                        onRevokeAccess(submission.id)
                      }}
                    >
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 20 20'
                        fill='none'
                        stroke='#EF4444'
                        strokeWidth='2.2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <path d='M5 5l10 10M15 5L5 15'></path>
                      </svg>
                      {isRevoking
                        ? 'Revoking...'
                        : isRevoked
                        ? 'Access Revoked'
                        : isExpired
                        ? 'Access Expired'
                        : 'Revoke Access'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className='status-section'>
              <span
                className={`wrap ${
                  isViewed
                    ? 'viewed'
                    : isRevoked
                    ? 'revoked'
                    : isExpired
                    ? 'expired'
                    : isNotSent
                    ? 'warning'
                    : 'pending'
                }`}
              >
                {isViewed && (
                  <>
                    <svg width='11' height='11' viewBox='0 0 20 20' fill='none'>
                      <defs>
                        <linearGradient id='seenG' x1='0' y1='0' x2='1' y2='0'>
                          <stop offset='0%' stopColor='#F0A9D8'></stop>
                          <stop offset='50%' stopColor='#BB9AE4'></stop>
                          <stop offset='100%' stopColor='#53BCFF'></stop>
                        </linearGradient>
                      </defs>
                      <path
                        d='M1.5 10S4.5 4 10 4s8.5 6 8.5 6-3 6-8.5 6S1.5 10 1.5 10z'
                        stroke='url(#seenG)'
                        strokeWidth='1.8'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      ></path>
                      <circle
                        cx='10'
                        cy='10'
                        r='2.5'
                        stroke='url(#seenG)'
                        strokeWidth='1.8'
                      ></circle>
                    </svg>
                    <span className='status'>
                      {`Seen by ${submission.viewedBy ?? 'agent'} - ${formatDayMonth(
                        submission.viewedAt
                      )}`}
                    </span>
                  </>
                )}
                {isAwaitingEmail && (
                  <>
                    <i className='bi bi-envelope-question' aria-hidden='true'></i>
                    Pending Email Collection
                  </>
                )}
                {isNotSent && (
                  <>
                    <i className='bi bi-exclamation-circle' aria-hidden='true'></i> Not Sent
                  </>
                )}
                {isRevoked && (
                  <>
                    <svg
                      width='11'
                      height='11'
                      viewBox='0 0 20 20'
                      fill='none'
                      stroke='#EF4444'
                      strokeWidth='2.4'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <path d='M5 5l10 10M15 5L5 15'></path>
                    </svg>
                    <span className='status'>ACCESS REVOKED</span>
                  </>
                )}
                {isExpired && (
                  <>
                    <i className='bi bi-hourglass-bottom' aria-hidden='true'></i>
                    <span className='status'>ACCESS EXPIRED</span>
                  </>
                )}
                {!isViewed && !isAwaitingEmail && !isNotSent && !isRevoked && !isExpired && (
                  <>
                    <i className='bi bi-clock' aria-hidden='true'></i>
                    Not yet viewed
                  </>
                )}
              </span>
            </div>
            <div className='agent-section'>
              <span className='label'>{hasMultipleAgents ? 'Agents: ' : 'Agent: '}</span>
              <strong className='value'>{agentSummary}</strong>
            </div>
            {!isRevoked ? (
              <div className='contact-section'>
                <button
                  type='button'
                  className='call'
                  disabled={callContacts.length === 0}
                  onClick={() => setContactAction('call')}
                >
                  <svg
                    width='13'
                    height='13'
                    viewBox='0 0 20 20'
                    fill='none'
                    stroke='var(--ink-700)'
                    strokeWidth='1.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M15.5 13l-2.5-1.2a1 1 0 00-1.1.2l-1 1a10 10 0 01-4-4l1-1a1 1 0 00.2-1.1L6.9 4.5a1 1 0 00-1.1-.5l-2 .5a1.2 1.2 0 00-.9 1.2C3.3 11.4 8.6 16.7 14.3 17a1.2 1.2 0 001.2-.9l.5-2a1 1 0 00-.5-1.1z'></path>
                  </svg>{' '}
                  Call{getContactCountLabel(callContacts.length)}
                </button>
                {emailContacts.length > 0 ? (
                  <button
                    type='button'
                    className='email'
                    disabled={emailContacts.length === 0}
                    onClick={() => setContactAction('email')}
                  >
                    <svg
                      width='13'
                      height='13'
                      viewBox='0 0 20 20'
                      fill='none'
                      stroke='#fff'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <rect x='2.5' y='4.5' width='15' height='11' rx='1.5'></rect>
                      <path d='M3 6l7 5 7-5'></path>
                    </svg>{' '}
                    Email{getContactCountLabel(emailContacts.length)}
                  </button>
                ) : (
                  <div className='noemail'>
                    {getNoEmailMessage(submission, contacts, isLandlineFlow)}
                  </div>
                )}
              </div>
            ) : (
              <div className='restore-section'>
                <button
                  className='restore'
                  type='button'
                  disabled={!canRestore}
                  onClick={() => {
                    if (!canRestore) {
                      return
                    }

                    onRestoreAccess(submission.id)
                  }}
                >
                  <svg
                    width='13'
                    height='13'
                    viewBox='0 0 20 20'
                    fill='none'
                    stroke='#111827'
                    strokeWidth='1.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M15.5 10A5.5 5.5 0 115 6.5M14 4v3h-3'></path>
                  </svg>{' '}
                  {isRestoring
                    ? 'Restoring...'
                    : hasExpiredAccessWindow
                    ? 'Access expired'
                    : 'Restore access'}
                </button>
              </div>
            )}
          </div>
        </div>
      </article>

      {contactAction && (
        <ApplicationContactModal
          contactAction={contactAction}
          submissionId={submission.id}
          contacts={contactAction === 'call' ? callContacts : emailContacts}
          onClose={() => setContactAction(null)}
        />
      )}
    </>
  )
}

export default ApplicationsTab
